// React
import React, { useEffect, useState } from "react";

// Mui
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  IconButton,
  Button,
  Alert,
  TextField,
  Stack,
} from "@mui/material";

// Styles
import "./PDFMediaDialog.scss";

// Assets
import cross from "../../assets/close-circle.svg";
import edit from "../../assets/edit.svg";

// Config
import { appConfig } from "../../config/appConfig";
import { UPLOADTYPE } from "../../models/enums/globalEnums";

interface PDFMediaDialogProps {
  open: boolean;
  onClose: () => void;
  oldMedia?: any;
  setOldMedia?: any;
  oldThumbnail?: any;
  setOldThumbnail?: any;
  gridType?: any;
  setGridType?: any;
  newMedia?: any;
  setNewMedia?: any;
  newThumbnail?: any;
  setNewThumbnail?: any;
  setgridTitle?: any;
  gridTitle?: any;
  editingIndex?: any;
}

const PDFMediaDialog: React.FC<PDFMediaDialogProps> = ({
  open,
  onClose,
  oldMedia,
  setOldMedia,
  oldThumbnail,
  setOldThumbnail,
  gridType,
  setGridType,
  newMedia,
  setNewMedia,
  newThumbnail,
  setNewThumbnail,
  setgridTitle,
  gridTitle,
  editingIndex,
}) => {
  // States
  const [gridImages, setgridImages] = useState<string>("");
  const [gridThumbnail, setgridThumbnail] = useState<string>("");

  const [selectedThumbnail, setSelectedThumbnail] = useState<File[]>([]);
  const [selectedVideos, setSelectedVideos] = useState<File[]>([]);
  const [selectgridtitle, setselectgridtitle] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [fileName, setfileName] = useState("No PDF");
  const [thumbnailpreview, setthumbnailpreview] =
    useState<string>("No Thumbnail");

  // Constants
  const MAX_pdfFILE_SIZE_MB = 15;
  const ALLOWED_pdfTYPE = "application/pdf";
  const MAX_FILE_SIZE_MB = 2;
  const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png"];

  useEffect(() => {
    if (oldMedia) {
      setgridImages(oldMedia);

      let extractedFileName = "";

      if (Array.isArray(oldMedia)) {
        if (oldMedia.length > 0) {
          extractedFileName = oldMedia[0].split("/").pop()?.trim() ?? "";
        }
      } else if (typeof oldMedia === "string" && oldMedia.length > 0) {
        extractedFileName = oldMedia.split("/").pop()?.trim() ?? "";
      }

      setfileName(extractedFileName);
    }

    if (oldThumbnail) {
      setgridThumbnail(oldThumbnail);
      setthumbnailpreview(
        `${appConfig.serviceUrls.commonURL}${oldThumbnail.trim()}`
      );
    }
    if (gridTitle) {
      setselectgridtitle(gridTitle);
    }

    if (newMedia && Array.isArray(newMedia) && newMedia.length > 0) {
      setSelectedVideos(newMedia);
      setfileName(newMedia[0].name);
    }
    if (newThumbnail) {
      setSelectedThumbnail(newThumbnail);
      if (Array.isArray(newThumbnail) && newThumbnail.length > 0) {
        setSelectedThumbnail(newThumbnail);
        setthumbnailpreview(URL.createObjectURL(newThumbnail[0]));
      }
    }
    if (newThumbnail instanceof File || newThumbnail instanceof Blob) {
      setthumbnailpreview(URL.createObjectURL(newThumbnail));
    }
  }, [oldMedia, oldThumbnail, gridTitle, newMedia, newThumbnail]);

  const handleClose = () => {
    onClose();
    setgridThumbnail("");
    setgridImages("");
    setSelectedThumbnail([]);
    setSelectedVideos([]);
    setselectgridtitle("");

    setfileName("");
  };

  const handleAdd = () => {
    setOldMedia(gridImages);
    setGridType(UPLOADTYPE.PDF);
    setOldThumbnail(gridThumbnail);

    setNewMedia(selectedVideos);
    setNewThumbnail(selectedThumbnail);
    setgridTitle(selectgridtitle);

    handleClose();
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "application/pdf"
  ) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      if (type === "application/pdf") {
        setSelectedVideos(filesArray);
      }
    }
  };

  const handlePdfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];

    if (files.length === 0) {
      setError("Please select a PDF file.");
      return;
    }

    const file = files[0]; // Assuming single file upload for PDF

    setfileName(file.name);

    if (file.type !== ALLOWED_pdfTYPE) {
      setError("Only .pdf files are allowed.");
      return;
    }

    if (file.size > MAX_pdfFILE_SIZE_MB * 1024 * 1024) {
      setError("File size must be less than or equal to 10MB.");
      return;
    }

    setError(null);
    handleFileChange(e, "application/pdf");
  };

  const handleThumbnailUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    if (fileExtension === "jfif") {
      setError(`Thumbnail type .jfif is not allowed: ${file.name}`);
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError(`Invalid Thumbnail type: ${file.name}`);
      return;
    }

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setError(`Thumbnail size of ${file.name} must be ≤ 2MB.`);
      return;
    }

    setSelectedThumbnail([file]);
    setthumbnailpreview(URL.createObjectURL(file));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setselectgridtitle(e.target.value);
  };

  return (
    <Dialog
      open={open}
      maxWidth={false}
      fullWidth
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "65vw",
          height: "85vh",
          maxWidth: "65vw",
          maxHeight: "90vh",
          overflow: "hidden",
          backgroundColor: "#fff",
        },
      }}
    >
      <DialogContent
        className="body1"
        sx={{
          padding: "0px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* HEADER */}
        <Box className="header1">
          <Typography className="uploaded-text"> Add PDF </Typography>

          <IconButton onClick={() => handleClose()} className="close-icon">
            <img src={cross} alt="visible" className="custom-icon2" />
          </IconButton>
        </Box>

        {/* BODY */}
        <Box
          className="Pdf-wrapper"
          sx={{
            display: "flex",
            gap: "20px",
            padding: "20px",
            flex: 1,
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          {/* LEFT PANEL */}
          <Box className="left-panel">
            <Box className="thumbnail-frame">
              {selectedThumbnail.length > 0 ? (
                <img
                  src={thumbnailpreview}
                  alt="New Thumbnail"
                  width={206}
                  height={206}
                />
              ) : gridThumbnail ? (
                <img
                  src={gridThumbnail}
                  alt="Existing Thumbnail"
                  width={206}
                  height={206}
                />
              ) : (
                <Typography>No thumbnail</Typography>
              )}
            </Box>

            <Button className="edit-thumbnail" component="label">
              <img
                src={edit}
                alt="edit"
                style={{ width: "20px", height: "20px", paddingRight: "5px" }}
              />
              <Typography className="edit-thumb-text">
                Edit Thumbnail
              </Typography>
              <input
                type="file"
                hidden
                accept="image/jpeg, image/png"
                onChange={handleThumbnailUpload}
              />
            </Button>
          </Box>

          {/* RIGHT PANEL */}

          <Box
            className="right-side"
            sx={{
              display: "flex",
              flexDirection: "column",
              // justifyContent: "space-around",
              alignItems: "center",
              background: "#FFF",
              padding: "30px 16px",
            }}
          >
            <Box>
              <Typography className="text-style">PDF Name</Typography>
              <Box className="pdf-name-wrapper1">
                <TextField
                  placeholder="Enter PDF Name"
                  size="small"
                  name="description"
                  value={selectgridtitle}
                  onChange={handleChange}
                  variant="standard"
                  fullWidth
                  InputProps={{ disableUnderline: true }}
                  inputProps={{
                    maxLength: 40,
                  }}
                />
              </Box>
              <Typography className="pdf-name-count1234 ">
                {selectgridtitle?.length || 0}/40
              </Typography>
            </Box>
            <Box>
              <Typography className="text-style">Upload PDF File</Typography>
              <Box className="pdf-upload-row">
                <Box className="pdf-name-wrapper">
                  <TextField
                    placeholder="Select PDF"
                    size="small"
                    name="pdfName"
                    value={fileName}
                    onChange={handleChange}
                    variant="standard"
                    fullWidth
                    disabled={true}
                    InputProps={{ disableUnderline: true }}
                  />
                </Box>

                <Box>
                  <Button component="label" className="choose-pdf-button">
                    <Typography className="pdf-button-text ">Browse</Typography>
                    <input
                      id="pdfUpload"
                      type="file"
                      accept="application/pdf"
                      hidden
                      onChange={handlePdfUpload}
                    />
                  </Button>
                </Box>
              </Box>

              {error && <Alert severity="error">{error}</Alert>}
            </Box>
          </Box>
        </Box>
        {/* Action button ////////////////////////// */}
        <Stack
          className="action-button"
          direction="row"
          spacing={2}
          justifyContent="flex-end"
        >
          <Button onClick={() => handleClose()} className="cancel-button">
            Cancel
          </Button>
          {editingIndex !== -1 && (
            <Button
              // disabled={error != null || viderror != null}
              onClick={() => handleAdd()}
              className="save-button"
            >
              Add
            </Button>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default PDFMediaDialog;
